import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import {
  type Feature as FeatureObject,
  FeatureType,
  type WaitingFeature,
} from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import useTooltipState from '@/app/components/search/hooks/useTooltipState.ts';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { Loader } from '@/app/components/search/components/EvolutionCard/Loaders/styled.ts';
import { useReopenGuard } from '@/app/common/hooks/useReopenGuard.ts';
import { IconOrLegacyAsset } from '@/app/common/icons/IconOrLegacyAsset';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { FeatureContainer, FeatureIcon, FeatureLink, FeatureText } from './styled';

type FeatureProps = FeatureObject & {
  testId?: string;
};

const DEFAULT_FEATURE_ICON = { category: 'Semantics', name: 'icon-check', color: 'iconPrimary' } as const;

const BRAND_TINTED_FEATURES: FeatureType[] = [FeatureType.primary, FeatureType.offer];
const tintIcon = <T extends StarfishIconRef | string | undefined>(featureType: FeatureType | undefined, icon: T): T =>
  icon && typeof icon === 'object' && featureType && BRAND_TINTED_FEATURES.includes(featureType)
    ? ({ ...icon, color: 'iconPrimaryDefault' } as T)
    : icon;

const isTravelExtraPack = (className?: string) => className?.includes('travel-extra-pack');
const isAlternativeFlexibleDates = (className?: string) => className?.includes('dates-more');

const Feature = ({
  icon,
  text,
  featureType,
  iconRight,
  Tooltip,
  Modal,
  Popover,
  className,
  testId,
  link,
  callback,
  gridSide,
  order,
  ...rest
}: FeatureProps) => {
  const isClient = useIsClient();
  const { localization } = usePageWrapperData();
  const { isMobile, isDesktop } = useCommonClient();
  const { curiosityClickEvents } = useCuriosityManager();
  const featureRef = useRef<HTMLButtonElement>(null);
  const [featureEl, setFeatureEl] = useState<HTMLButtonElement | null>(null);

  useLayoutEffect(() => setFeatureEl(featureRef.current), []);

  const tooltip = useTooltipState({ isMobile });
  const [modalOpen, setModalOpen] = useState(false);
  const reopenGuard = useReopenGuard();

  useOnclickOutside(
    () => {
      if (Tooltip && tooltip.isOpen) tooltip.close();
    },
    // @ts-ignore
    { refs: [{ current: featureEl }] }
  );

  const openModal = useCallback(() => {
    if (reopenGuard.blocked()) return;
    if (isAlternativeFlexibleDates(className)) curiosityClickEvents.alternativeFlexibleDatesOpenModal();
    if (isTravelExtraPack(className)) curiosityClickEvents.travelExtraPackOpenModal();
    setModalOpen(true);
  }, [className, curiosityClickEvents, reopenGuard]);

  const closeModal = useCallback(
    (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isAlternativeFlexibleDates(className)) curiosityClickEvents.alternativeFlexibleDatesCloseModal();
      if (isTravelExtraPack(className)) curiosityClickEvents.travelExtraPackCloseModal();
      e?.preventDefault();
      e?.stopPropagation();
      reopenGuard.markClosed();
      setModalOpen(false);
    },
    [className, curiosityClickEvents, reopenGuard]
  );

  const onHoverEnter = () => {
    if (!isDesktop || !Tooltip) return;
    tooltip.scheduleOpen();
  };

  const onHoverLeave = () => {
    if (!isDesktop || !Tooltip) return;
    tooltip.scheduleClose();
  };

  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (featureEl && !featureEl.contains(e.nativeEvent.target as Node)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (callback || Modal || Tooltip) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (callback) {
      callback();
      return;
    }
    if (Modal) {
      openModal();
      return;
    }
    if (Tooltip) {
      if (isMobile) tooltip.toggle();
      else tooltip.open();
    }
  };

  const onLinkClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    link?.();
  };

  const onBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    if (Tooltip && tooltip.isOpen && !e.currentTarget.contains(e.relatedTarget as Node)) {
      tooltip.close();
    }
  };

  const isInteractive = !!(callback || Modal || Popover || Tooltip);
  const isGray = featureType === FeatureType.gray;
  const cleanText = text?.replace(/<[^>]*>/g, '');

  const renderIcon = () => {
    if (icon === '') return null;
    return (
      <FeatureIcon>
        <IconOrLegacyAsset icon={tintIcon(featureType, icon || DEFAULT_FEATURE_ICON)} size={20} />
      </FeatureIcon>
    );
  };

  const renderText = () => {
    if (!text) return null;
    const color = isGray ? 'textSecondary' : 'textPrimary';

    if (Tooltip) {
      return (
        <FeatureText className="text" variant="BodySmall" color={color}>
          <Tooltip
            isOpen={tooltip.isOpen}
            isMobile={isMobile}
            onMouseEnter={onHoverEnter}
            onMouseLeave={onHoverLeave}
          />
        </FeatureText>
      );
    }

    if (iconRight) {
      return (
        <FeatureText className="text" variant="BodySmall" color={color}>
          <AddElementToLastWordInString text={text}>
            <IconOrLegacyAsset icon={tintIcon(featureType, iconRight)} className="icon-right" size={16} />
          </AddElementToLastWordInString>
        </FeatureText>
      );
    }

    return (
      <FeatureText className="text" variant="BodySmall" dangerouslySetInnerHTML={{ __html: text }} color={color} />
    );
  };

  if (featureType === FeatureType.waiting) {
    const { loaderWidth = 180, loaderHeight = 20 } = rest as WaitingFeature;
    return (
      <FeatureContainer
        as="div"
        variant="BodySmall"
        featureType={featureType}
        localization={localization}
        className={className}
        isInteractive={false}
        gridSide={gridSide}
        order={order}
      >
        <Loader elWidth={loaderWidth} elHeight={loaderHeight} className={className} />
      </FeatureContainer>
    );
  }

  if (!text) return null;

  const containerProps = {
    ref: featureRef as React.RefObject<HTMLButtonElement | null>,
    featureType,
    localization,
    className,
    'data-testid': testId,
    onClick,
    onBlur,
    isInteractive,
    gridSide,
    order,
    ...(isInteractive
      ? { type: 'button' as const, ...(cleanText && { 'aria-label': cleanText }), tabIndex: 0 }
      : { as: 'div' as const, disabled: true, tabIndex: -1 }),
  };

  if (link) {
    return (
      <FeatureLink
        {...containerProps}
        onClick={onLinkClick}
        type="button"
        {...(cleanText && { 'aria-label': cleanText })}
      >
        {renderIcon()}
        {renderText()}
        {isClient && Modal && <Modal isOpen={modalOpen} onClose={closeModal} />}
        {isClient && Popover && <Popover anchorEl={featureEl} />}
      </FeatureLink>
    );
  }

  return (
    <FeatureContainer as="button" variant="BodySmall" {...containerProps}>
      {renderIcon()}
      {renderText()}
      {isClient && Modal && <Modal isOpen={modalOpen} onClose={closeModal} />}
      {isClient && Popover && <Popover anchorEl={featureEl} />}
    </FeatureContainer>
  );
};

export default Feature;
